import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import eventsApi, { type Event } from '@/src/api/eventsApi';
import { useSessionUser } from '@/src/lib/sessionStore';

/**
 * useFetchEvent — the `GET /events/{id}` fetch behind an event's routes.
 *
 * Called ONCE per event, by [[eventCart]] (the provider in
 * `app/event/[id]/_layout.tsx`), not by the screens: the detail page and the
 * confirm step both read the result off that context, so moving between them
 * costs no request.
 *
 * ── The event belongs to a VIEWER, not just an id ─────────────────────────
 * `GET /events/{id}` is answered against the token the request carried, so the
 * event is a function of (id, viewer) — not of the id alone. Every tier's
 * `bookability` is the server's verdict for THIS reader: signed out, that's
 * `unauthenticated`; signed in, it's the real one (bookable, seats exhausted,
 * membership required). Same URL, different body.
 *
 * So the session is a DEPENDENCY of this fetch, and it's held here rather than
 * left to callers. The alternative is every component that renders a fetched
 * event watching the session and calling `retry` — which is the same subscription
 * written N times, and wrong the first time someone forgets it: the screen keeps
 * drawing "Sign in to book" on a tier the user can now buy, and the only sign
 * anything is stale is that the CTA does nothing useful.
 *
 * This works because [[sessionStore]] persists the token BEFORE it notifies —
 * `startSession` awaits the AsyncStorage write, `endSession` awaits the delete —
 * and [[api]] reads the token off disk per request. By the time a new user
 * reaches this hook, the refetch it triggers already carries the new credential.
 *
 * The event arrives EXACTLY as [[eventsApi]] declares it — this hook adds no
 * mapping of its own. Anything the screen wants shaped differently (the slot↔tier
 * matrix, lifecycle flags) is derived at the call site or in the data layer, not
 * smuggled in here.
 *
 * ── What it returns ──────────────────────────────────────────────────────
 * A discriminated union so the screen's three-way branch is total: `loading` →
 * `error` → `success`, with `event` non-null only on success.
 *
 * `error` is the whole `ApiError`, not a boolean, for the reason
 * [[useHomePageEvents]] spells out: the view draws a different thing per KIND
 * (connection copy for 'network', generic for 'server'), and only the error
 * object survives the trip out of the data layer carrying that. Today
 * `EventErrorPage` shows `message` — which is already the right words per kind
 * (see apiError.ts) — but the kind is there for a view that wants to treat them
 * differently. Anything caught goes through `asApiError`, so `kind` is always
 * readable even when the throw came from our own mapping rather than the server.
 *
 * `retry` re-runs the fetch: the Retry button on that error page. It's on the
 * state rather than beside it so the whole contract travels as one value through
 * the cart context, which hands screens `eventState` and nothing else. `refresh`
 * is the same request for pull-to-refresh — see both fields below.
 */
export type FetchEventState = (
  | { status: 'loading'; event: null; error: null }
  | { status: 'error'; event: null; error: PSErrorClass }
  | { status: 'success'; event: Event; error: null }
) & {
  /** Re-run the fetch — straight back to `loading` while it's in the air. */
  retry: () => void;
  /**
   * Re-run the fetch with the page still drawn, resolving when it settles —
   * pull-to-refresh, which brings its own spinner and has to be told when to stop.
   *
   * The difference from `retry` matters more here than on the other fetch hooks:
   * `loading` swaps the WHOLE detail screen for a skeleton, so a `retry` behind a
   * pull would unmount the page mid-gesture and bring it back scrolled to the top
   * of the hero — exactly the mount-continuity problem the user-change path below
   * is written to avoid. This leaves the drawn event alone until the new one lands.
   */
  refresh: () => Promise<void>;
};

/**
 * A settled fetch result, tagged with the `id` it belongs to. Kept internal —
 * the hook derives the public `FetchEventState` (below) from it: a result whose
 * `id` no longer matches the current one reads as `loading`, so we never have to
 * synchronously reset state to `loading` inside the effect on an `id` change.
 */
type SettledResult =
  | { status: 'error'; event: null; error: PSErrorClass; id: string }
  | { status: 'success'; event: Event; error: null; id: string };

const LOADING = {
  status: 'loading',
  event: null,
  error: null,
} as const;

/**
 * Fetches the event for `id`, re-running whenever `id` OR the signed-in user
 * changes. Starts in `loading`, then settles into `success` (with the event) or
 * `error`.
 *
 * The settled result is stored WITH the `id` it's for. The returned state is
 * derived: if no result yet, or the stored result is for a stale `id` (a fast
 * re-navigation), it reads `loading`. This keeps the effect a pure subscription —
 * no synchronous `setState('loading')` on every `id` change — while still
 * showing loading immediately for a new id. The `run` counter additionally drops
 * a late resolution belonging to a previous id, user or attempt.
 *
 * ── Why the viewer is a dependency but NOT part of that staleness tag ──
 * The two changes mean different things, so they get different behaviour:
 *
 *   - a new `id` invalidates what's on screen — it's a different event, and
 *     nothing drawn from the old one is worth keeping. Hence `loading`.
 *   - a new user re-evaluates the SAME event. The last result stays on screen
 *     until the new one lands, and swaps in place.
 *
 * What that buys is mount continuity at the worst possible moment. `loading`
 * swaps the whole detail screen for `EventDetailSkeleton` (see
 * `app/event/[id]/index.tsx`), so `EventBody` unmounts and comes back scrolled to
 * the top of the hero. The one flow where a user change happens on this page is
 * the sign-in the page itself prompts — a tier reading `unauthenticated`, tapped,
 * auth modal, done — so the user is returning to the tier list they were just
 * looking at, about to book. Bouncing them to the top of a skeleton is the wrong
 * answer to "I signed in". The cart survives either way (it lives in
 * `_layout.tsx`, above this screen), so scroll and paint are the whole stake.
 *
 * The cost is one render pass where the page is INCONSISTENT: the screen's own
 * `useSessionUser` already reads the new user while the tier cards still draw the
 * previous viewer's verdicts, so a tap on a stale "Sign in to book" can re-open
 * auth for someone already signed in. It lasts one request. If that gets noticed,
 * the fix is a `isRevalidating` flag on this state for the CTAs to disable
 * against — NOT a fourth `status`, which would force every consumer of this
 * union to grow a branch for a transient.
 *
 * Depending on the user OBJECT, not a field off it: the store hands out one
 * reference per session write, so this fires on sign-in, sign-out, and the
 * profile PATCH that completes a signup — all three can move a `bookability`
 * verdict. Cold-start restore also writes, but the splash gate in
 * `app/_layout.tsx` holds every route until it's done, so no event screen is
 * mounted to see it.
 *
 * `retry` clears that stored result, which is what puts the state back to
 * `loading`, and bumps the attempt counter the effect keys off — so a retry that
 * fails the same way still arrives as a fresh `ApiError`, and an effect watching
 * it fires again rather than seeing an unchanged value. Both it and `refresh` are
 * the hook's manual re-ask for the changes the session can't see: buying the
 * membership a tier demands moves bookability without touching the user.
 *
 * ── Why the guard is a counter and not the old `active` flag ──
 * `refresh` is called from OUTSIDE the effect (a screen's pull), so it can't see
 * an effect-local `let active`. `run` is bumped on every effect re-run and on
 * unmount, and each fetch remembers the number it started under, which keeps the
 * property the flag had and adds the one it couldn't: a refresh whose event lands
 * after the id or the viewer changed is DROPPED rather than written. That matters
 * because a stale write here isn't just stale — a result tagged with the previous
 * id reads through the derivation below as `loading`, so it would flip the screen
 * to a skeleton, and one carrying the previous viewer's verdicts would beat the
 * new user's answer purely by arriving second.
 */
export function useFetchEvent(id: string): FetchEventState {
  const user = useSessionUser();
  const [result, setResult] = useState<SettledResult | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Which run's answer is still wanted — see the docstring.
  const run = useRef(0);

  const load = useCallback(async (fetchId: string, forRun: number) => {
    try {
      const event = await eventsApi.getEventById(fetchId);
      if (run.current === forRun)
        setResult({ status: 'success', event, error: null, id: fetchId });
    } catch (error: unknown) {
      if (run.current === forRun)
        setResult({
          status: 'error',
          event: null,
          error: asApiError(error),
          id: fetchId,
        });
    }
  }, []);

  useEffect(() => {
    const forRun = (run.current += 1);
    load(id, forRun);

    // If `id` or the viewer changes (or the screen unmounts) before this
    // resolves, the bump invalidates the run and the late result is ignored.
    return () => {
      run.current += 1;
    };
  }, [id, user, attempt, load]);

  const retry = useCallback(() => {
    setResult(null);
    setAttempt((n) => n + 1);
  }, []);

  // Joins whichever run is current instead of starting one, so it's invalidated
  // by the same bump: pull, navigate away mid-flight, and nothing is written.
  const refresh = useCallback(() => load(id, run.current), [id, load]);

  // Derived: loading until a result for THIS id has settled.
  const state = result && result.id === id ? result : LOADING;

  // Memoised so the state keeps a stable identity between renders — the cart
  // context builds its value off this one, and a fresh object every render would
  // re-render every screen reading the cart.
  return useMemo(() => ({ ...state, retry, refresh }), [state, retry, refresh]);
}
